import { IconInfoCircle } from "@tabler/icons-react";

import type { Capacity } from "../../api/types";
import {
  poolsBySize,
  familyLabel,
  summarizeCapacity,
  summarizePool,
} from "../../lib/capacity";
import { pluralize } from "../../lib/format";
import { GpuLegend } from "./GpuPills";
import { NodeRow } from "./NodeRow";

interface CapacitySectionProps {
  capacity: Capacity;
}

/**
 * The fleet as it really is: pool by pool, machine by machine, GPU by GPU.
 *
 * Args:
 *     capacity: The measured capacity payload.
 *
 * Returns:
 *     The fleet section of the home screen.
 */
export function CapacitySection({ capacity }: CapacitySectionProps): JSX.Element {
  const { pools_error, nodes, telemetry } = capacity;
  const pools = poolsBySize(capacity);
  const stuck = pools.reduce((sum, pool) => sum + pool.unavailable_nodes, 0);
  const poolReadFailures = pools.filter((pool) => pool.error !== null);

  return (
    <section className="section" aria-labelledby="capacity-title">
      <div className="section__head">
        <h2 className="section__title" id="capacity-title">
          The fleet
        </h2>
        <span className="section__meta">{summarizeCapacity(capacity)}</span>
      </div>

      {pools.length > 0 ? (
        <ul className="pool-list">
          {pools.map((pool) => (
            <li key={pool.pool_id} className="pool-line">
              <span className="pool-line__shape">
                {pool.gpus_per_node}-GPU
                {pool.family ? ` ${familyLabel(pool.family)}` : ""} machines
              </span>
              <span className="pool-line__name mono">{pool.name ?? "—"}</span>
              <span className="pool-line__state">
                {pool.error ?? summarizePool(pool)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {pools_error ? (
        <p className="notice">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>{pools_error}</span>
        </p>
      ) : null}

      {poolReadFailures.length > 0 ? (
        <p className="notice">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>
            {poolReadFailures.length === 1
              ? "One machine shape could not be checked with Scaleway, so whether a machine of it can be started is unknown."
              : "Some machine shapes could not be checked with Scaleway, so whether machines of them can be started is unknown."}{" "}
            The GPU numbers above are still exact.
          </span>
        </p>
      ) : null}

      {stuck > 0 ? (
        <p className="notice">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>
            {pluralize(stuck, "machine")} {stuck === 1 ? "is" : "are"} stuck —
            neither running nor starting. The platform team has to look; the
            console asks for a replacement rather than waiting for{" "}
            {stuck === 1 ? "it" : "them"}.
          </span>
        </p>
      ) : null}

      {!telemetry.ok ? (
        <p className="notice">
          <IconInfoCircle size={14} aria-hidden="true" />
          <span>
            {/* The server's reason is a complete sentence written for this
                audience; it stands on its own instead of being nested inside
                a parenthetical mid-sentence. */}
            {telemetry.reason ??
              "How busy each GPU is cannot be measured right now, so that " +
                "number is left blank."}{" "}
            While this lasts, unused GPUs are <strong>not</strong> taken back
            automatically — nothing is measured, so nothing is reclaimed. If
            you have finished with yours, stop the workspace.
          </span>
        </p>
      ) : null}

      {nodes.length === 0 ? (
        <p className="capacity__empty">
          No machine is running right now — that is normal, not broken. Ask for
          GPUs and one is started for you; machines nobody uses are stopped.
        </p>
      ) : (
        <ul className="node-list">
          {nodes.map((node, index) => (
            <NodeRow
              key={node.name ?? `starting-${index}`}
              node={node}
              etaMinutes={capacity.node_start_eta_minutes}
            />
          ))}
        </ul>
      )}

      <GpuLegend />
    </section>
  );
}
